import { Play } from "lucide-react";
import { useCallback, useState } from "react";

import { trackVideoPlay } from "lib/analytics";
import { cn } from "products/muggleTestV0/lib/utils";

const YOUTUBE_VIDEO_ID = "ZblxVfNnwZg";
const YOUTUBE_VIDEO_TITLE = "Muggle Test product walkthrough";
const YOUTUBE_EMBED_SRC =
  `https://www.youtube-nocookie.com/embed/${YOUTUBE_VIDEO_ID}` +
  `?autoplay=1&playsinline=1&rel=0&modestbranding=1`;
// YouTube auto-derives thumbnail URLs from the video ID. `maxresdefault` is the
// HD frame; it only exists if the source was uploaded ≥720p (this one is), and
// falls back gracefully — a missing file renders the grey YouTube placeholder
// rather than breaking the page.
const POSTER_SRC = `https://i.ytimg.com/vi/${YOUTUBE_VIDEO_ID}/maxresdefault.jpg`;
const POSTER_ALT = `${YOUTUBE_VIDEO_TITLE} — click to play`;

/**
 * Product walkthrough — lazy-loaded YouTube iframe behind a clickable poster.
 *
 * The poster-then-iframe ("facade") pattern is what fixes the
 * "Sign in to confirm you are not a bot" challenge YouTube serves to
 * autoplay embeds without a user gesture. The visitor's click is the trust
 * signal YouTube's anti-abuse layer wants, and we also avoid shipping the
 * full YouTube player JS on every landing-page view.
 */
export function HowItWorksVideo() {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const handlePlay = useCallback(() => {
    trackVideoPlay(YOUTUBE_VIDEO_ID, YOUTUBE_VIDEO_TITLE);
    setIsPlaying(true);
  }, []);

  return (
    <div
      className={cn(
        "aspect-video w-full overflow-hidden rounded-2xl border border-accent/10 bg-zinc-950",
        "shadow-lg shadow-accent/[0.08] ring-1 ring-white/5",
      )}
    >
      {isPlaying ? (
        <iframe
          className="h-full w-full"
          src={YOUTUBE_EMBED_SRC}
          title={YOUTUBE_VIDEO_TITLE}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          onClick={handlePlay}
          className="group relative block h-full w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
          aria-label={`Play ${YOUTUBE_VIDEO_TITLE}`}
        >
          <img
            src={POSTER_SRC}
            alt={POSTER_ALT}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
          <span
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/30"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform group-hover:scale-110 sm:h-20 sm:w-20">
              <Play className="ml-1 h-7 w-7 fill-zinc-900 text-zinc-900 sm:h-8 sm:w-8" />
            </span>
          </span>
        </button>
      )}
    </div>
  );
}
